import React from 'react'
import Card from './Card'
import Image from 'next/image'
import Render from 'core/layout/Render'
import Grid, { Cell } from 'core/styles/Grid'
import SwiperCore, { Pagination } from 'swiper'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import { Title } from 'core/styles/Title'
import { Container } from 'core/styles/Container'
import { InvertedCta } from 'core/styles/Cta'
import { sup } from 'core/helpers/string'
import { light } from 'core/helpers/color'
import { Tabs, Tab, Panel } from 'core/styles/Tabs'
import { Social } from '../layout/Footer'
import { Button } from '../layout/Common'
import { breakpoint } from '../layout/styles'
import { useMediaQuery } from '../helpers/mq'
import { Swiper, SwiperSlide } from 'swiper/react'

SwiperCore.use([Pagination])

const handlePrint = () => {
  try {
    // First check if we're in a browser environment
    if (typeof window === 'undefined') {
      console.warn('Print functionality is not available in server-side environment');
      return;
    }

    // Check if we're in a WebKit environment (iOS WebView, etc.)
    if (window.webkit && 
        window.webkit.messageHandlers && 
        window.webkit.messageHandlers.print &&
        typeof window.webkit.messageHandlers.print.postMessage === 'function') {
      // Use native WebKit print handler
      window.webkit.messageHandlers.print.postMessage('print');
      return;
    }

    // Use standard browser print function
    if (window.print && typeof window.print === 'function') {
      window.print();
      return;
    }

    // No print support available
    console.error('Print functionality is not available in this environment.');
    
  } catch (error) {
    console.error('Error occurred while trying to print:', error);
    
    // Enhanced fallback with additional error handling
    if (typeof window !== 'undefined' && window.print && typeof window.print === 'function') {
      try {
        window.print();
      } catch (fallbackError) {
        console.error('Fallback print also failed:', fallbackError);
      }
    }
  }
};

const Recipe = ({ item: recipe, social, translations }: any) => {
  const [selectedImage, setSelectedImage] = React.useState(recipe.images?.[0])
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0)
  const [selectedTabIndex, setSelectedTabIndex] = React.useState<number>(0)
  const { locale } = useRouter()
  const [product, setProduct] = React.useState(recipe.product?.[0])
  const [productResolved, setProductResolved] = React.useState(false)
  const background = product?.background
  const desktop = useMediaQuery(breakpoint.desktop)

  // Reset product state when the recipe or locale changes (client-side locale switch)
  React.useEffect(() => {
    setProduct(recipe.product?.[0])
    setProductResolved(false)
  }, [recipe.uid, locale])

  React.useEffect(() => {
    if (!product || product._content_type_uid !== 'syndigo_product' || !product.syndigo_id) {
      setProductResolved(true)
      return
    }

    // AbortController cancels the fetch if the recipe or locale changes before
    // the response arrives, preventing setProduct/setProductResolved being called
    // on a stale effect cycle (e.g. fast locale switch on the recipe page).
    const controller = new AbortController()
    const { signal } = controller

    fetch(
      `/api/syndigo/product?id=${product.syndigo_id}&locale=${locale === 'fr' ? 'fr-ca' : locale}&transform=true`,
      { signal }
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (signal.aborted) return
        if (resp?.[0]?.title) {
          setProduct(resp[0])
        } else {
          setProduct(null)
        }
        setProductResolved(true)
      })
      .catch((error) => {
        if (signal.aborted) return
        console.error('[Recipe] Syndigo product fetch failed:', error)
        setProduct(null)
        setProductResolved(true)
      })

    return () => controller.abort()
  }, [product?.syndigo_id, locale])

  const [previewLoaded, setPreviewLoaded] = React.useState(true)
  const selectImage = (image: any, index: number) => {
    setPreviewLoaded(false)
    setSelectedImage(image)
    setSelectedImageIndex(index)
  }

  return (
    <Wrapper>
      <Container noprint>
        <InvertedCta href={locale === 'en-US' ? '/recipes' : `/${locale}/recipes`}>{translations.recipe.all_recipes}</InvertedCta>
      </Container>
      <Container full>
        <Grid>
          {desktop && (
            <Cell md={6} noprint>
              <Preview background={light(background)}>
                <div className="image-container" style={{ opacity: previewLoaded ? 1 : 0, transition: 'opacity 400ms ease' }}>
                  <Image
                    src={`${selectedImage?.url}?height=600&auto=webp&format=webp&quality=75`}
                    alt={recipe.title || 'Recipe image'}
                    layout="fill"
                    objectFit="cover"
                    priority
                    onLoadingComplete={() => setPreviewLoaded(true)}
                  />
                </div>
              </Preview>
            </Cell>
          )}
          <Cell
            md={6}
            gap={1}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              textAlign: 'center'
            }}>
            <Summary>
              <Title dangerouslySetInnerHTML={{ __html: sup(recipe.title) }} center={true} />

              {!desktop && (
                <Swiper
                  style={{ height: 360, paddingBottom: 48 }}
                  pagination={{ clickable: true }}
                  slidesPerView={1}
                  spaceBetween={24}
                  centeredSlides={true}>
                  {recipe.images?.map((image: any, index: number) => (
                    <SwiperSlide key={image.uid}>
                      <div className="image-swiper-container">
                        <Image
                          src={`${image.url}?height=600&auto=webp&format=webp&quality=75`}
                          alt={recipe.title || 'Recipe image'}
                          layout="fill"
                          objectFit="cover"
                          priority={index === 0}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}

              <Description>{recipe.description}</Description>

              <PrepInfo>
                {recipe.preparation.serves && (
                  <PrepItem>
                    <img src="/images/serves.svg" alt="serves" aria-hidden="true" role="icon" />
                    {translations.recipe.serves}: {recipe.preparation.serves}
                  </PrepItem>
                )}
                {recipe.preparation.prep_time && (
                  <PrepItem>
                    <img src="/images/prep.svg" alt="prep" aria-hidden="true" role="icon" />
                    {translations.recipe.prep_time}: {recipe.preparation.prep_time}
                  </PrepItem>
                )}
                {recipe.preparation.cook_time && (
                  <PrepItem>
                    <img src="/images/cook.svg" alt="cook" aria-hidden="true" role="icon" />
                    {translations.recipe.cook_time}: {recipe.preparation.cook_time}
                  </PrepItem>
                )}
              </PrepInfo>

              <Share>
                <Print>
                  <Button onClick={handlePrint}>{translations.recipe.print_recipe}</Button>
                </Print>

                <Social margin={8} center>
                  {social?.social_share?.map((item: any) => (
                    <a href={item.link.href} key={item._metadata.uid} target="_blank" rel="noopener noreferrer">
                      <img src={`${item.icon.url}?auto=svg`} alt={item.link.title} />
                    </a>
                  ))}
                </Social>
              </Share>
            </Summary>

            <Thumbnails>
              {recipe.images.map((image: any, index: number) => (
                <Thumbnail
                  selected={selectedImageIndex === index}
                  background={background}
                  key={image.uid}
                  onClick={() => selectImage(image, index)}>
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <Image
                      src={image.url}
                      alt={`Thumbnail ${index + 1}`}
                      layout="fill"
                      sizes="(max-width: 992px) 80px, 160px"
                      objectFit="cover"
                    />
                  </div>
                </Thumbnail>
              ))}
            </Thumbnails>
          </Cell>
        </Grid>
      </Container>
      <Details>
        <Grid>
          <Cell md={9}>
            <Tabs style={{ display: 'flex' }}>
              <Tab onClick={() => setSelectedTabIndex(0)} selected={selectedTabIndex === 0}>
                {translations.recipe.ingredients_tab}
              </Tab>
              <Tab onClick={() => setSelectedTabIndex(1)} selected={selectedTabIndex === 1}>
                {translations.recipe.preparation_tab}
              </Tab>
            </Tabs>
            <Panel selected={selectedTabIndex === 0}>
              {recipe.ingredients.map((ingredient_list: any) => (
                <div key={ingredient_list._metadata.uid} style={{ marginTop: 40, marginRight: 48 }}>
                  {ingredient_list.title && <h2>{ingredient_list.title}</h2>}
                  <ul>
                    {ingredient_list.items.map((ingredient: any, index: number) => (
                      <li key={`ing${index}`}>{ingredient}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </Panel>
            <Panel selected={selectedTabIndex === 1}>
              <Steps>
                {recipe.preparation.tips && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 24
                    }}>
                    <div style={{ margin: '0 20px' }}>
                      <img src="/images/info.svg" alt="info" aria-hidden="true" role="icon" />
                    </div>
                    <div>
                      <b>{translations.recipe.tips_from_the_chef}:</b> {recipe.preparation.tips}
                    </div>
                  </div>
                )}
                <ol>
                  {recipe.preparation.step.map((step: any, index: number) => (
                    <li key={`step${index}`}>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </Steps>
            </Panel>
          </Cell>
          <Cell md={3} gap={1.5} noprint>
            {product && productResolved && product.url_token && product.filters?.[0]?.value && (
              <Card
                {...{
                  title: product.title,
                  link: `/products/${product.filters[0].value}/${product.url_token}`,
                  image: product.images && product.images[0],
                  path: 'products',
                  color: product.color?.[0]?.background
                }}
                style={{
                  marginTop: '36px',
                  '--border-color': product.color?.[0]?.color
                }}
              />
            )}
          </Cell>
        </Grid>
      </Details>
      {recipe.page_components?.map((entry: any) => (
        <Render
          key={entry.uid}
          pageComponents={entry.page_components.components}
          contentTypeUid="page"
          translations={translations}
          entryUid={recipe.uid}
          locale={recipe.locale}
        />
      ))}
    </Wrapper>
  )
}
export default Recipe

const Wrapper = styled.div`
  padding: 0 1.5rem;
  @media ${breakpoint.desktop} {
    padding: 0;
  }
`

export const Share = styled.div`
  display: flex;
  flex-direction: column;
`

export const Print = styled.div`
  margin: 24px 0;
  @media print {
    display: none;
  }
`

const Steps = styled.div`
  @media ${breakpoint.desktop} {
    margin-top: 40px;
    margin-right: 32px;
  }
`

export const Details = styled(Container)`
  margin-top: 92px;
  @media print {
    margin-top: 0;
  }
`

export const Summary = styled.div`
  max-width: 540px;
  width: 100%;
  @media print {
    max-width: 680px;
  }
  ${Title} {
    margin-bottom: 2rem;
  }
`

export const PrepInfo = styled.div`
  color: var(--text);
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  @media ${breakpoint.desktop} {
    flex-direction: row;
  }
  @media print {
    flex-direction: row;
  }
`

export const PrepItem = styled.div`
  margin: 0.75rem;
  @media ${breakpoint.desktop} {
    margin: 0 0.75rem;
  }
  display: flex;
  align-items: center;
  img {
    margin-right: 8px;
  }
`

const Thumbnails = styled.div`
  margin-top: 24px;
  display: none;
  @media ${breakpoint.desktop} {
    display: flex;
  }
  @media print {
    display: none;
  }
`

const Thumbnail = styled.div<any>`
  margin-bottom: 15px;
  margin-right: 15px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  ${(props) =>
    props.background &&
    css`
      background: ${props.background};
    `}
  ${(props) =>
    props.selected &&
    css`
      &:after {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        border: 3px var(--primary) solid;
        box-sizing: border-box;
        top: 0;
        left: 0;
      }
    `}
`

export const Preview = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  ${(props) =>
    props.background &&
    css`
      background: ${props.background};
    `}

  .image-container {
    position: relative;
    width: 100%;
    height: 600px;
    min-height: 320px;
  }

  @media ${breakpoint.desktop} {
    .image-container,
    .image-swiper-container {
      height: 600px;
      min-height: 320px;
      width: 100%;
    }
  }
`

export const Description = styled.div<any>`
  color: var(--text);
  margin: 3rem 0;
  font-size: 16px;
  @media ${breakpoint.desktop} {
    margin: 2rem 0;
  }
  &:first-child {
    margin-top: 0;
  }
`
